import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { supportTicketService } from '../../../services/supportTicketService';

const TicketDetailsModal = ({ ticket, onClose, onStatusUpdate, isStaff, userId }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadMessages();
  }, [ticket?.id]);

  const loadMessages = async () => {
    setLoading(true);
    const result = await supportTicketService?.getTicketMessages(ticket?.id);
    if (result?.success) {
      setMessages(result?.messages || []);
    }
    setLoading(false);
  };

  const handleSendMessage = async () => {
    if (!newMessage?.trim() || sending) return;

    setSending(true);
    const result = await supportTicketService?.addMessage(
      ticket?.id,
      userId,
      newMessage?.trim(),
      isStaff ? 'staff' : 'member'
    );

    if (result?.success) {
      setNewMessage('');
      await loadMessages();
    }
    setSending(false);
  };

  const handleStatusChange = async (newStatus) => {
    await onStatusUpdate(ticket?.id, newStatus);
    onClose();
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'text-blue-500',
      medium: 'text-yellow-500',
      high: 'text-orange-500',
      urgent: 'text-red-500'
    };
    return colors?.[priority] || colors?.medium;
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        onClick={(e) => e?.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
                {ticket?.subject}
              </h2>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <span className={`font-medium ${getPriorityColor(ticket?.priority)}`}>
                  {ticket?.priority?.toUpperCase()} Priority
                </span>
                <span>•</span>
                <span>{ticket?.category?.replace('_', ' ')}</span>
                <span>•</span>
                <span>{ticket?.status?.replace('_', ' ')?.toUpperCase()}</span>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              iconName="X"
              iconSize={20}
            />
          </div>

          {/* AI Analysis */}
          {ticket?.ai_analysis && (
            <div className="mt-4 bg-primary/10 border border-primary/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="Sparkles" size={18} className="text-primary mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-foreground mb-1">AI Analysis</p>
                  <p className="text-sm text-muted-foreground">
                    {ticket?.ai_analysis?.analysis_summary}
                  </p>
                  <div className="flex items-center gap-4 mt-2 text-xs">
                    <span>Sentiment: {ticket?.ai_analysis?.sentiment}</span>
                    <span>Complexity: {ticket?.ai_analysis?.complexity_score}/10</span>
                    <span>Department: {ticket?.ai_analysis?.suggested_department?.replace('_', ' ')}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Original Description */}
          <div className="bg-background border border-border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="User" size={16} className="text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">
                {ticket?.user_profiles?.full_name || 'Member'}
              </span>
              <span className="text-xs text-muted-foreground">• Original Request</span>
            </div>
            <p className="text-sm text-foreground">{ticket?.description}</p>
          </div>

          {/* Message Thread */}
          {loading ? (
            <div className="text-center py-8">
              <Icon name="Loader2" size={24} className="animate-spin mx-auto text-primary" />
            </div>
          ) : (
            messages?.map((message) => (
              <div
                key={message?.id}
                className={`rounded-lg p-4 ${
                  message?.sender_type === 'system' || message?.is_automated ?'bg-primary/10 border border-primary/20'
                    : message?.sender_type === 'staff' ?'bg-green-500/10 border border-green-500/20' :'bg-background border border-border'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon
                    name={message?.sender_type === 'system' ? 'Bot' : message?.sender_type === 'staff' ? 'Shield' : 'User'}
                    size={16}
                    className="text-muted-foreground"
                  />
                  <span className="text-sm font-medium text-foreground">
                    {message?.sender_type === 'system' ?'AI Assistant'
                      : message?.sender_type === 'staff' ?'Support Staff' : message?.user_profiles?.full_name ||'Member'}
                  </span>
                  {message?.is_automated && (
                    <span className="text-xs px-2 py-0.5 bg-primary/20 text-primary rounded-full">
                      Automated
                    </span>
                  )}
                </div>
                <p className="text-sm text-foreground whitespace-pre-wrap">{message?.message}</p>
              </div>
            ))
          )}
        </div>

        {/* Message Input */}
        <div className="p-6 border-t border-border">
          <div className="flex gap-3">
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e?.target?.value)}
              placeholder="Type your message..."
              rows={3}
              className="flex-1 px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <div className="flex flex-col gap-2">
              <Button
                onClick={handleSendMessage}
                disabled={!newMessage?.trim() || sending}
                loading={sending}
                iconName="Send"
                size="icon"
              />
              {isStaff && ticket?.status !== 'resolved' && (
                <Button
                  onClick={() => handleStatusChange('resolved')}
                  variant="success"
                  size="icon"
                  iconName="CheckCircle2"
                  title="Mark as Resolved"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketDetailsModal;